export type BootstrapStage =
  | "shell"
  | "auth"
  | "user"
  | "jwt"
  | "route"
  | "ready";

export const BOOTSTRAP_STAGE_FLOORS: Record<BootstrapStage, number> = {
  auth: 0.25,
  jwt: 0.65,
  ready: 1,
  route: 0.85,
  shell: 0.05,
  user: 0.45,
};

const STAGE_ORDER: BootstrapStage[] = [
  "shell",
  "auth",
  "user",
  "jwt",
  "route",
  "ready",
];

/** Fraction of remaining gap closed per second while waiting between stages. */
const CREEP_RATE_PER_SECOND = 0.35;

/** Never reach the next stage floor via creep alone — leave headroom for the milestone. */
const CREEP_HEADROOM = 0.02;

export type BootstrapProgress = {
  getProgress: () => number;
  getStage: () => BootstrapStage;
  reset: () => void;
  /** Jump to a stage floor in one notification (allows rewind from ready). */
  rewindTo: (stage: BootstrapStage) => void;
  setStage: (stage: BootstrapStage) => void;
  subscribe: (listener: (progress: number) => void) => () => void;
  tick: (nowMs?: number) => number;
};

export type CreateBootstrapProgressOptions = {
  now?: () => number;
};

function stageIndex(stage: BootstrapStage): number {
  return STAGE_ORDER.indexOf(stage);
}

function ceilingFor(stage: BootstrapStage): number {
  if (stage === "ready") {
    return 1;
  }
  const next = STAGE_ORDER[stageIndex(stage) + 1];
  return BOOTSTRAP_STAGE_FLOORS[next] - CREEP_HEADROOM;
}

export function createBootstrapProgress(
  options: CreateBootstrapProgressOptions = {},
): BootstrapProgress {
  const nowFn = options.now ?? (() => Date.now());

  let stage: BootstrapStage = "shell";
  let value = BOOTSTRAP_STAGE_FLOORS.shell;
  let lastTickAt = nowFn();
  const listeners = new Set<(progress: number) => void>();

  const notify = () => {
    for (const listener of listeners) {
      listener(value);
    }
  };

  const setValue = (next: number) => {
    const clamped = Math.min(1, Math.max(value, next));
    if (clamped === value) {
      return;
    }
    value = clamped;
    notify();
  };

  return {
    getProgress: () => value,
    getStage: () => stage,
    reset: () => {
      stage = "shell";
      value = BOOTSTRAP_STAGE_FLOORS.shell;
      lastTickAt = nowFn();
      notify();
    },
    rewindTo: (nextStage) => {
      stage = nextStage;
      value = BOOTSTRAP_STAGE_FLOORS[nextStage];
      lastTickAt = nowFn();
      notify();
    },
    setStage: (nextStage) => {
      // Same-stage repeats (e.g. StrictMode double-invoked effects) must not
      // reset lastTickAt, or redundant calls would stall the creep clock.
      if (stageIndex(nextStage) <= stageIndex(stage)) {
        return;
      }
      stage = nextStage;
      setValue(BOOTSTRAP_STAGE_FLOORS[nextStage]);
      lastTickAt = nowFn();
      // Floor may equal current value (e.g. already crept there); still notify stage consumers via progress.
      notify();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    tick: (nowMs) => {
      const at = nowMs ?? nowFn();
      if (stage === "ready") {
        lastTickAt = at;
        return value;
      }
      const elapsedSeconds = Math.max(0, (at - lastTickAt) / 1000);
      lastTickAt = at;
      if (elapsedSeconds === 0) {
        return value;
      }
      const ceiling = ceilingFor(stage);
      if (value >= ceiling) {
        return value;
      }
      const remaining = ceiling - value;
      const delta =
        remaining * (1 - Math.exp(-CREEP_RATE_PER_SECOND * elapsedSeconds));
      setValue(value + delta);
      return value;
    },
  };
}

/** Process-wide singleton so spinner remounts continue the same progress. */
export const bootstrapProgress = createBootstrapProgress();
